import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import ArchiveCard from "@/components/feature/ArchiveCard";
import { useSeo, SITE_ORIGIN } from "@/hooks/useSeo";
import { categories, events, siteInfo } from "@/mocks/archive";

export default function Archive() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCat = searchParams.get("cat") || "all";
  const urlQuery = searchParams.get("q") || "";
  const [query, setQuery] = useState(urlQuery);
  const [sort, setSort] = useState<"year-desc" | "year-asc" | "default">("year-desc");

  const jsonLd = useMemo(
    () => [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "档案库",
        url: `${SITE_ORIGIN}/archive`,
        inLanguage: "zh-CN",
        description:
          "暗线档案的完整档案库，按历史灾难、未解之谜、文明兴衰与科技事故分类陈列全部事件档案。",
        isPartOf: { "@type": "WebSite", name: siteInfo.name, url: SITE_ORIGIN },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: SITE_ORIGIN },
          { "@type": "ListItem", position: 2, name: "档案库", item: `${SITE_ORIGIN}/archive` },
        ],
      },
    ],
    []
  );

  useSeo({
    title: "档案库 | 历史灾难 · 未解之谜 · 文明兴衰 · 科技事故档案",
    description:
      "暗线档案档案库汇集已整理的全部暗黑系事件档案，涵盖历史灾难、未解之谜、文明兴衰与科技事故四大分类，支持分类筛选与关键词检索，快速定位你关心的时间线。",
    keywords: "暗黑档案库,历史灾难档案,未解之谜,文明兴衰,科技事故,暗黑史资料",
    path: "/archive",
    jsonLd,
  });

  const filtered = useMemo(() => {
    const kw = query.trim().toLowerCase();
    let list = events.filter((event) => {
      const matchCat = activeCat === "all" || event.category === activeCat;
      if (!matchCat) return false;
      if (!kw) return true;
      const haystack = [
        event.title,
        event.summary,
        event.location,
        event.yearLabel,
        ...event.tags,
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(kw);
    });
    list = [...list];
    if (sort === "year-desc") list.sort((a, b) => b.year - a.year);
    if (sort === "year-asc") list.sort((a, b) => a.year - b.year);
    return list;
  }, [activeCat, query, sort]);

  const setCategory = (id: string) => {
    const next = new URLSearchParams(searchParams);
    if (id === "all") next.delete("cat");
    else next.set("cat", id);
    setSearchParams(next, { replace: true });
  };

  const clearAll = () => {
    setQuery("");
    setSort("year-desc");
    const next = new URLSearchParams();
    setSearchParams(next, { replace: true });
  };

  return (
    <SiteLayout>
      <section className="w-full px-4 md:px-8 pt-28 md:pt-32 pb-10 border-b border-background-200">
        <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "档案库" }]} />
        <h1 className="mt-6 font-heading text-3xl md:text-5xl font-black text-foreground-950">
          档案库
        </h1>
        <p className="mt-4 max-w-3xl text-sm md:text-base leading-relaxed text-foreground-500">
          全部已整理的暗黑系事件档案集中在这里。可以按分类筛选，也可以直接输入关键词，检索标题、地点、年份与标签。
        </p>
      </section>

      <section className="w-full px-4 md:px-8 py-10">
        <div className="flex flex-col lg:flex-row gap-6 lg:items-end justify-between">
          <div className="w-full lg:max-w-md">
            <label htmlFor="archive-search" className="block text-xs text-foreground-500 mb-2">
              关键词检索
            </label>
            <div className="relative">
              <i
                className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-500"
                aria-hidden="true"
              ></i>
              <input
                id="archive-search"
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="输入事件名称、地点或年份"
                className="w-full pl-9 pr-4 py-2.5 rounded-md text-sm bg-background-100 border border-background-200 text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/30"
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <div>
              <span className="block text-xs text-foreground-500 mb-2">排序方式</span>
              <div className="inline-flex p-1 rounded-full bg-background-100 border border-background-200">
                {[
                  { id: "year-desc", label: "年份降序" },
                  { id: "year-asc", label: "年份升序" },
                  { id: "default", label: "默认顺序" },
                ].map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => setSort(option.id as typeof sort)}
                    className={`px-3.5 py-1.5 rounded-full text-xs whitespace-nowrap cursor-pointer transition-colors ${
                      sort === option.id
                        ? "bg-primary-500 text-background-950"
                        : "text-foreground-600 hover:text-foreground-900"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setCategory("all")}
            className={`px-3.5 py-1.5 rounded-full text-xs whitespace-nowrap cursor-pointer border transition-colors ${
              activeCat === "all"
                ? "bg-primary-500 text-background-950 border-primary-500"
                : "border-background-300 text-foreground-600 hover:border-primary-700 hover:text-foreground-900"
            }`}
          >
            全部分类
          </button>
          {categories.map((cat) => (
            <button
              key={cat.id}
              type="button"
              onClick={() => setCategory(cat.id)}
              className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs whitespace-nowrap cursor-pointer border transition-colors ${
                activeCat === cat.id
                  ? "bg-primary-500 text-background-950 border-primary-500"
                  : "border-background-300 text-foreground-600 hover:border-primary-700 hover:text-foreground-900"
              }`}
            >
              <i className={cat.icon} aria-hidden="true"></i>
              {cat.name}
            </button>
          ))}
        </div>

        <div className="mt-8 flex items-center justify-between border-b border-background-200 pb-4">
          <p className="text-sm text-foreground-500">
            共找到 <span className="text-foreground-900">{filtered.length}</span> 条档案
          </p>
          <button
            type="button"
            onClick={clearAll}
            className="inline-flex items-center gap-1 text-xs text-foreground-500 hover:text-primary-400 cursor-pointer transition-colors"
          >
            <i className="ri-refresh-line" aria-hidden="true"></i>
            重置筛选
          </button>
        </div>

        {filtered.length > 0 ? (
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((event) => (
              <ArchiveCard key={event.id} event={event} />
            ))}
          </div>
        ) : (
          <div className="py-20 text-center">
            <span className="mx-auto w-14 h-14 flex items-center justify-center rounded-full bg-background-100 text-foreground-500">
              <i className="ri-inbox-archive-line text-2xl" aria-hidden="true"></i>
            </span>
            <p className="mt-4 text-sm text-foreground-600">
              没有匹配的档案，试试更换关键词或重置筛选条件。
            </p>
            <button
              type="button"
              onClick={clearAll}
              className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm whitespace-nowrap cursor-pointer border border-background-300 text-foreground-800 hover:border-primary-700 transition-colors"
            >
              重置筛选
            </button>
          </div>
        )}
      </section>

      <section className="w-full px-4 md:px-8 py-14 border-t border-background-200 bg-background-100">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
              想按时间顺序通读？
            </h2>
            <p className="mt-2 text-sm text-foreground-500">
              暗黑时间轴按纪元与年份排列全部条目，更适合连续阅读与脉络梳理。
            </p>
          </div>
          <Link
            to="/timeline"
            className="shrink-0 inline-flex items-center gap-2 px-6 py-3 rounded-md text-sm whitespace-nowrap cursor-pointer bg-primary-500 text-background-950 hover:bg-primary-400 transition-colors"
          >
            <i className="ri-git-branch-line" aria-hidden="true"></i>
            打开暗黑时间轴
          </Link>
        </div>
      </section>
    </SiteLayout>
  );
}